/**
 * Initial Setup Page
 * First-time account and user creation
 */

import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { apiClient } from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'
import { AuthShell, Field, Notice, Spinner } from './authParts'

export function SetupPage() {
  const navigate = useNavigate()
  const { checkSession } = useAuth()

  const [formData, setFormData] = useState({
    accountName: '',
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
  })
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'accountName' | 'displayName' | 'email' | 'password' | 'confirmPassword', string>>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [setupCompleted, setSetupCompleted] = useState(false)

  // セットアップ完了済みの場合はログインページにリダイレクト
  useEffect(() => {
    async function checkSetupStatus() {
      try {
        const response = await apiClient.get<{ needsSetup: boolean }>('/auth/check-setup')
        if (response.success && !response.data?.needsSetup) {
          setSetupCompleted(true)
          // 3秒後にログインページへ自動リダイレクト
          setTimeout(() => {
            navigate('/login', { replace: true })
          }, 3000)
        }
      } catch (error) {
        console.error('Failed to check setup status:', error)
      }
    }
    checkSetupStatus()
  }, [navigate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    setError('')
    setFieldErrors(prev => ({ ...prev, [e.target.name]: undefined }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validation（項目ごとに入力欄の下へ「何が違うか・どう直すか」を出す）
    const errs: typeof fieldErrors = {}
    if (!formData.accountName) errs.accountName = 'アカウント名を入力してください'
    if (!formData.displayName) errs.displayName = '表示名を入力してください'
    if (!formData.email) errs.email = 'メールアドレスを入力してください'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'メールアドレスの形式が正しくありません。例: your@email.com'
    if (!formData.password) errs.password = 'パスワードを入力してください'
    else if (formData.password.length < 8) errs.password = '8文字以上で入力してください'
    if (formData.password && formData.password !== formData.confirmPassword) errs.confirmPassword = '上のパスワードと一致していません。同じものを入力してください'
    setFieldErrors(errs)
    if (Object.keys(errs).length > 0) return

    setIsLoading(true)

    try {
      const response = await apiClient.post('/auth/setup', {
        accountName: formData.accountName,
        email: formData.email,
        password: formData.password,
        displayName: formData.displayName,
      })

      if (response.success) {
        // Auto-login after setup
        await checkSession()
        navigate('/', { replace: true })
      } else {
        setError(response.error || 'セットアップに失敗しました')
      }
    } catch (err) {
      setError('セットアップ中にエラーが発生しました')
    } finally {
      setIsLoading(false)
    }
  }

  // セットアップ完了済みの場合、別のメッセージを表示
  if (setupCompleted) {
    return (
      <AuthShell title="セットアップは完了しています">
        <Notice tone="info">初期セットアップはすでに済んでいます。まもなくログイン画面に移動します。</Notice>
        <Link to="/login" className="gm-btn gm-btn-primary gm-btn-block">ログイン画面へ</Link>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="初期セットアップ" lead="最初の管理アカウントを作成します。">
      <form onSubmit={handleSubmit} className="grid gap-4" noValidate>
        <Field id="accountName" type="text" label="アカウント名（会社名・店舗名）" value={formData.accountName} onChange={handleChange} disabled={isLoading} autoFocus error={fieldErrors.accountName} />
        <Field id="displayName" type="text" label="表示名" value={formData.displayName} onChange={handleChange} placeholder="お名前" autoComplete="name" disabled={isLoading} error={fieldErrors.displayName} />
        <Field id="email" type="email" label="メールアドレス" value={formData.email} onChange={handleChange} placeholder="your@email.com" autoComplete="email" disabled={isLoading} error={fieldErrors.email} />
        <Field id="password" type="password" label="パスワード" value={formData.password} onChange={handleChange} hint="8文字以上" autoComplete="new-password" disabled={isLoading} error={fieldErrors.password} />
        <Field id="confirmPassword" type="password" label="パスワード（確認）" value={formData.confirmPassword} onChange={handleChange} autoComplete="new-password" disabled={isLoading} error={fieldErrors.confirmPassword} />

        {error && <Notice tone="bad">{error}</Notice>}

        <button type="submit" disabled={isLoading} className="gm-btn gm-btn-primary gm-btn-block">
          {isLoading ? <Spinner /> : null}
          <span>{isLoading ? '作成しています' : 'セットアップを完了する'}</span>
        </button>
      </form>
    </AuthShell>
  )
}
